/**
 * Pixel Manager — top-level menu page for orchestrating Meta Pixels
 * across all of the operator's landing pages and exporting audiences
 * for cross-BM rebuild scenarios (banned account recovery).
 *
 * Tabs:
 *   1. "Pixels" — inventory + 24h health + promote/add/remove actions
 *   2. "Audience Export" — date-range CSV download for Meta Customer
 *      File upload
 *
 * Hard-design rules followed here:
 *   - Every destructive action (remove pixel) requires confirmation.
 *   - Add/promote actions show preview of "will affect N pages" before
 *     applying.
 *   - Health badges use traffic-light colors based on success rate
 *     (≥99% green / ≥90% amber / <90% red / no-data gray).
 */

import React, { useEffect, useState, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { UserLayout } from '../components/User/UserLayout';
import { useAuthStore } from '../store/authStore';
import { useEffectivePlan } from '../store/accountStore';
import { CapiReplayPanel } from '../components/Common/CapiReplayPanel';
import { getMaxFacebookPixels, hasFeature } from '../constants/planLimits';
import {
  Target,
  Activity,
  Plus,
  Trash2,
  ArrowUpCircle,
  Download,
  AlertCircle,
  CheckCircle2,
  Loader2,
  Star,
  Eye,
  Calendar,
  RefreshCw,
  ShieldAlert,
  FileDown,
  Info,
} from 'lucide-react';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

function getAuthToken(): string {
  return useAuthStore.getState().token || localStorage.getItem('token') || '';
}

interface PixelPageRef {
  pageId: string;
  pageName: string;
  pageSlug: string;
  position: number;
  isPrimary: boolean;
}

interface PixelHealth {
  ok24h: number;
  fail24h: number;
  successRate: number | null;
  lastAt: string | null;
}

interface PixelInventoryItem {
  pixelId: string;
  role: 'primary' | 'shadow' | 'mixed';
  usedOnPages: number;
  primaryOn: number;
  shadowOn: number;
  pages: PixelPageRef[];
  /** True when the pixel is in the central registry (the catalog). */
  inRegistry: boolean;
  health: PixelHealth | null;
}

interface InventoryResponse {
  pixels: PixelInventoryItem[];
  totalPages: number;
  pagesWithoutPixel: number;
  accountCapiEnabled: boolean;
  registryCount: number;
}

type TabId = 'pixels' | 'audience' | 'replay';

export const PixelManager: React.FC = () => {
  const { t } = useTranslation('tracking');
  const effectivePlan = useEffectivePlan();
  const [tab, setTab] = useState<TabId>('pixels');
  const canAudience = hasFeature(effectivePlan, 'audienceExport');
  const canReplay = hasFeature(effectivePlan, 'capiReplay');

  return (
    <UserLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <header className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Target className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('pixel.title')}</h1>
              <p className="text-sm text-gray-600">{t('pixel.subtitle')}</p>
            </div>
          </div>
        </header>

        {/* Tab strip */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="flex gap-1">
            <TabButton active={tab === 'pixels'} onClick={() => setTab('pixels')} icon={<Activity className="w-4 h-4" />}>
              {t('pixel.tabs.pixels')}
            </TabButton>
            {canAudience && (
              <TabButton active={tab === 'audience'} onClick={() => setTab('audience')} icon={<FileDown className="w-4 h-4" />}>
                {t('pixel.tabs.audience')}
              </TabButton>
            )}
            {canReplay && (
              <TabButton active={tab === 'replay'} onClick={() => setTab('replay')} icon={<RefreshCw className="w-4 h-4" />}>
                {t('pixel.tabs.replay')}
              </TabButton>
            )}
          </nav>
        </div>

        {tab === 'pixels' && <PixelsTab />}
        {tab === 'audience' && <AudienceTab />}
        {tab === 'replay' && <CapiReplayPanel />}
      </div>
    </UserLayout>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Tab strip button
// ────────────────────────────────────────────────────────────────────

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ active, onClick, icon, children }) => (
  <button
    onClick={onClick}
    className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-2 ${
      active
        ? 'border-blue-600 text-blue-600'
        : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
    }`}
  >
    {icon}
    {children}
  </button>
);

// ────────────────────────────────────────────────────────────────────
//  TAB 1 — Pixels
// ────────────────────────────────────────────────────────────────────

const PixelsTab: React.FC = () => {
  const { t, i18n } = useTranslation('tracking');
  const effectivePlan = useEffectivePlan();
  const pixelCap = getMaxFacebookPixels(effectivePlan);
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newPixelId, setNewPixelId] = useState('');
  const [busyPixelId, setBusyPixelId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<PixelInventoryItem | null>(null);

  async function loadInventory(silent = false) {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const res = await fetch(`${API_URL}/pixel-manager/inventory`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      if (!res.ok) throw new Error('failed to load');
      const data = await res.json();
      setInventory(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('pixel.toast.loadFailed'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    loadInventory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleAdd() {
    const trimmed = newPixelId.trim();
    if (!/^\d{10,20}$/.test(trimmed)) {
      toast.error(t('pixel.toast.invalidId'));
      return;
    }
    setBusyPixelId(trimmed);
    try {
      const res = await fetch(`${API_URL}/pixel-manager/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ pixelId: trimmed }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || t('pixel.toast.addFailed'));
      // Backend now writes only to the central registry — no fan-out
      // to landing pages. The user picks which pages use the pixel in
      // the Builder.
      if (data.alreadyRegistered) {
        toast(t('pixel.toast.alreadyRegistered'));
      } else {
        toast.success(t('pixel.toast.added'));
      }
      setNewPixelId('');
      setShowAddForm(false);
      await loadInventory(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('pixel.toast.addFailed'));
    } finally {
      setBusyPixelId(null);
    }
  }

  async function handlePromote(pixelId: string) {
    setBusyPixelId(pixelId);
    try {
      const res = await fetch(`${API_URL}/pixel-manager/promote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ pixelId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('pixel.toast.promoteFailed'));
      toast.success(t('pixel.toast.promoted', { count: data.pagesUpdated }));
      await loadInventory(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('pixel.toast.promoteFailed'));
    } finally {
      setBusyPixelId(null);
    }
  }

  async function handleRemove(pixelId: string) {
    setBusyPixelId(pixelId);
    try {
      const res = await fetch(`${API_URL}/pixel-manager/remove`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({ pixelId }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('pixel.toast.removeFailed'));
      // Tailor the toast: registry-only removal vs registry+pages
      // cleanup so the operator sees what actually happened.
      const pagesUpdated = Number(data.pagesUpdated) || 0;
      toast.success(
        pagesUpdated > 0
          ? t('pixel.toast.removed', { count: pagesUpdated })
          : t('pixel.toast.removedRegistry'),
      );
      setRemoveConfirm(null);
      await loadInventory(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('pixel.toast.removeFailed'));
    } finally {
      setBusyPixelId(null);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        {t('pixel.loading')}
      </div>
    );
  }

  const failoverValue =
    inventory && inventory.pixels.length >= 2
      ? t('pixel.summary.good')
      : inventory && inventory.pixels.length === 1
      ? t('pixel.summary.atRisk')
      : t('pixel.summary.noPixel');

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard
          icon={<Target className="w-5 h-5 text-blue-600" />}
          label={t('pixel.summary.inUse')}
          value={inventory?.pixels.length || 0}
          hint={t('pixel.summary.totalPages', { count: inventory?.totalPages || 0 })}
        />
        <SummaryCard
          icon={<Star className="w-5 h-5 text-amber-500" />}
          label={t('pixel.summary.withPrimary')}
          value={(inventory?.totalPages || 0) - (inventory?.pagesWithoutPixel || 0)}
          hint={
            inventory?.pagesWithoutPixel
              ? t('pixel.summary.withoutPixel', { count: inventory.pagesWithoutPixel })
              : t('pixel.summary.allCovered')
          }
          warn={!!(inventory?.pagesWithoutPixel && inventory.pagesWithoutPixel > 0)}
        />
        <SummaryCard
          icon={<ShieldAlert className="w-5 h-5 text-emerald-600" />}
          label={t('pixel.summary.failover')}
          value={failoverValue}
          hint={
            inventory && inventory.pixels.length >= 2
              ? t('pixel.summary.multi')
              : t('pixel.summary.addShadow')
          }
          warn={!inventory || inventory.pixels.length < 2}
        />
      </div>

      {/* Add pixel form / button */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-semibold text-gray-900">{t('pixel.addCard.title')}</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {pixelCap === -1
                ? t('pixel.addCard.subtitle')
                : t('pixel.addCard.capHint', {
                    used: inventory?.registryCount ?? inventory?.pixels.length ?? 0,
                    cap: pixelCap,
                    defaultValue: `Registered {{used}} / {{cap}} pixels on your plan`,
                  })}
            </p>
          </div>
          <button
            onClick={() => loadInventory(true)}
            disabled={refreshing}
            className="p-2 text-gray-500 hover:text-gray-900 disabled:opacity-50"
            title={t('pixel.addCard.refresh')}
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {showAddForm ? (
          <div className="space-y-3">
            <input
              type="text"
              value={newPixelId}
              onChange={(e) => setNewPixelId(e.target.value)}
              placeholder={t('pixel.addCard.placeholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
              autoComplete="off"
            />
            <p className="text-xs text-gray-500 flex items-start gap-1.5 leading-relaxed">
              <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
              {t('pixel.addCard.howItWorks')}
            </p>
            <div className="flex gap-2">
              <button
                onClick={handleAdd}
                disabled={!newPixelId.trim() || busyPixelId === newPixelId.trim()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-2"
              >
                {busyPixelId === newPixelId.trim() ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                {t('pixel.addCard.confirm')}
              </button>
              <button
                onClick={() => { setShowAddForm(false); setNewPixelId(''); }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm"
              >
                {t('pixel.addCard.cancel')}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            disabled={pixelCap !== -1 && (inventory?.registryCount ?? inventory?.pixels.length ?? 0) >= pixelCap}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            {t('pixel.addCard.addButton')}
          </button>
        )}
      </div>

      {/* Pixel list */}
      {inventory && inventory.pixels.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-3">
          {inventory?.pixels.map((p) => (
            <PixelRow
              key={p.pixelId}
              item={p}
              busy={busyPixelId === p.pixelId}
              onPromote={() => handlePromote(p.pixelId)}
              onRemove={() => setRemoveConfirm(p)}
              locale={i18n.language}
            />
          ))}
        </div>
      )}

      {/* Remove confirmation modal */}
      {removeConfirm && (
        <RemoveConfirmModal
          item={removeConfirm}
          busy={busyPixelId === removeConfirm.pixelId}
          onCancel={() => setRemoveConfirm(null)}
          onConfirm={() => handleRemove(removeConfirm.pixelId)}
        />
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Pixel row
// ────────────────────────────────────────────────────────────────────

const PixelRow: React.FC<{
  item: PixelInventoryItem;
  busy: boolean;
  onPromote: () => void;
  onRemove: () => void;
  locale: string;
}> = ({ item, busy, onPromote, onRemove, locale }) => {
  const { t } = useTranslation('tracking');
  const [expanded, setExpanded] = useState(false);

  const healthColor = (() => {
    if (!item.health) return 'bg-gray-100 text-gray-500';
    const r = item.health.successRate ?? 0;
    if (r >= 0.99) return 'bg-emerald-100 text-emerald-700';
    if (r >= 0.9) return 'bg-amber-100 text-amber-700';
    return 'bg-red-100 text-red-700';
  })();

  // Hide role badge entirely when the pixel is registered but unused —
  // "shadow on 0 pages" reads as misleading. Once it's actually wired
  // up to a page (via the Builder picker) the role badge reappears.
  const roleBadge = (() => {
    if (item.usedOnPages === 0) return null;
    switch (item.role) {
      case 'primary':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700">
            <Star className="w-3 h-3 fill-amber-500 stroke-amber-700" /> {t('pixel.row.primary')}
          </span>
        );
      case 'shadow':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 text-slate-700">
            <Eye className="w-3 h-3" /> {t('pixel.row.shadow')}
          </span>
        );
      case 'mixed':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-700">
            {t('pixel.row.mixed')}
          </span>
        );
    }
  })();

  // Surface "this pixel is only on a legacy page, never registered"
  // so the operator knows why it has no badge of its own.
  const orphanBadge = !item.inRegistry ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
      {t('pixel.row.orphan')}
    </span>
  ) : null;

  const usedOnLabel =
    item.usedOnPages === 0
      ? t('pixel.row.notUsed')
      : item.usedOnPages === 1
        ? t('pixel.row.usedOn', { count: item.usedOnPages })
        : t('pixel.row.usedOnPlural', { count: item.usedOnPages });

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <code className="text-base font-mono font-semibold text-gray-900">{item.pixelId}</code>
              {roleBadge}
              {orphanBadge}
            </div>
            <div className="mt-2 flex items-center gap-3 text-sm text-gray-600 flex-wrap">
              <span className={item.usedOnPages === 0 ? 'text-gray-500 italic' : undefined}>
                {usedOnLabel}
              </span>
              {item.primaryOn > 0 && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>{t('pixel.row.primaryOn', { count: item.primaryOn })}</span>
                </>
              )}
              {item.shadowOn > 0 && (
                <>
                  <span className="text-gray-300">·</span>
                  <span>{t('pixel.row.shadowOn', { count: item.shadowOn })}</span>
                </>
              )}
            </div>
            {item.usedOnPages === 0 && item.inRegistry && (
              <p className="mt-2 text-xs text-gray-500 flex items-start gap-1.5 leading-relaxed">
                <Info className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-blue-500" />
                {t('pixel.row.pickInBuilderHint')}
              </p>
            )}

            {/* 24h health badge */}
            {item.health ? (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${healthColor}`}>
                  <Activity className="w-3 h-3" />
                  {t('pixel.row.capi24', { ok: item.health.ok24h, fail: item.health.fail24h })}
                </span>
                {item.health.successRate !== null && (
                  <span className="text-xs text-gray-500">
                    {t('pixel.row.successRate', { pct: Math.round(item.health.successRate * 100) })}
                  </span>
                )}
                {item.health.lastAt && (
                  <span className="text-xs text-gray-400">
                    {t('pixel.row.lastEvent', { ts: new Date(item.health.lastAt).toLocaleString(locale) })}
                  </span>
                )}
              </div>
            ) : (
              <div className="mt-3 inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500">
                <Activity className="w-3 h-3" />
                {t('pixel.row.noEvents')}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Promote only makes sense when at least one page already
                uses the pixel as shadow — there's nothing to reorder
                when the pixel sits in the registry alone. */}
            {item.usedOnPages > 0 && item.role !== 'primary' && (
              <button
                onClick={onPromote}
                disabled={busy}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-amber-300 text-amber-700 hover:bg-amber-50 rounded-lg disabled:opacity-50"
                title={t('pixel.row.promoteTitle')}
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowUpCircle className="w-3.5 h-3.5" />}
                {t('pixel.row.promote')}
              </button>
            )}
            <button
              onClick={onRemove}
              disabled={busy}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 text-red-700 hover:bg-red-50 rounded-lg disabled:opacity-50"
              title={t('pixel.row.removeTitle')}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {t('pixel.row.remove')}
            </button>
            {/* Hide the "Pages" toggle entirely when the pixel is not
                yet attached anywhere — there's nothing to expand. */}
            {item.usedOnPages > 0 && (
              <button
                onClick={() => setExpanded(!expanded)}
                className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {expanded ? t('pixel.row.hide') : t('pixel.row.pages')}
              </button>
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
          <ul className="space-y-1.5 text-sm">
            {item.pages.map((p) => (
              <li key={p.pageId} className="flex items-center gap-2">
                <span className={`inline-flex items-center justify-center w-5 h-5 text-[10px] rounded-full ${p.isPrimary ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}`}>
                  {p.position + 1}
                </span>
                <span className="font-medium text-gray-800">{p.pageName}</span>
                <code className="text-xs text-gray-500">/{p.pageSlug}</code>
                {p.isPrimary && (
                  <Star className="w-3 h-3 fill-amber-400 stroke-amber-600" />
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Empty state
// ────────────────────────────────────────────────────────────────────

const EmptyState: React.FC = () => {
  const { t } = useTranslation('tracking');
  return (
    <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-10 text-center">
      <Target className="w-10 h-10 text-gray-400 mx-auto mb-3" />
      <h3 className="text-base font-semibold text-gray-900">{t('pixel.empty.title')}</h3>
      <p className="text-sm text-gray-600 mt-1 max-w-md mx-auto">{t('pixel.empty.hint')}</p>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  Summary card
// ────────────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number | string;
  hint?: string;
  warn?: boolean;
}> = ({ icon, label, value, hint, warn }) => (
  <div className={`bg-white border rounded-xl p-4 ${warn ? 'border-amber-300' : 'border-gray-200'}`}>
    <div className="flex items-center gap-2 mb-1.5">
      {icon}
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
    </div>
    <div className={`text-2xl font-bold ${warn ? 'text-amber-700' : 'text-gray-900'}`}>{value}</div>
    {hint && <div className="text-xs text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

// ────────────────────────────────────────────────────────────────────
//  Remove confirmation modal
// ────────────────────────────────────────────────────────────────────

const RemoveConfirmModal: React.FC<{
  item: PixelInventoryItem;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ item, busy, onCancel, onConfirm }) => {
  const { t } = useTranslation('tracking');
  const isInUse = item.usedOnPages > 0;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-5 h-5 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">
            {isInUse ? t('pixel.remove.title') : t('pixel.remove.titleRegistry')}
          </h3>
        </div>
        <div className="text-sm text-gray-700 space-y-2 mb-5">
          <p>
            <Trans
              i18nKey={isInUse ? 'pixel.remove.line1' : 'pixel.remove.line1Registry'}
              t={t}
              values={{ id: item.pixelId, count: item.usedOnPages }}
              components={{
                1: <code className="font-mono bg-gray-100 px-1.5 py-0.5 rounded" />,
              }}
            />
          </p>
          <p className="text-gray-600">
            <Trans
              i18nKey="pixel.remove.line2"
              t={t}
              components={{ strong: <strong /> }}
            />
          </p>
          {isInUse && (
            <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-xs">
              {t('pixel.remove.warning')}
            </p>
          )}
        </div>
        <div className="flex gap-2 justify-end">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          >
            {t('pixel.remove.cancel')}
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('pixel.remove.remove')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────────
//  TAB 2 — Audience Export
// ────────────────────────────────────────────────────────────────────

const AudienceTab: React.FC = () => {
  const { t } = useTranslation('tracking');
  const [days, setDays] = useState(30);
  const [includePii, setIncludePii] = useState(true);
  const [preview, setPreview] = useState<{ totalClicks: number; days: number } | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const presets = useMemo(() => [
    { label: t('audience.range.preset7'), value: 7 },
    { label: t('audience.range.preset30'), value: 30 },
    { label: t('audience.range.preset60'), value: 60 },
    { label: t('audience.range.preset90'), value: 90 },
  ], [t]);

  async function loadPreview() {
    setLoadingPreview(true);
    try {
      const res = await fetch(
        `${API_URL}/pixel-manager/audience-preview?days=${days}`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } },
      );
      if (!res.ok) throw new Error('preview failed');
      const data = await res.json();
      setPreview(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('audience.toast.previewFailed'));
    } finally {
      setLoadingPreview(false);
    }
  }

  useEffect(() => {
    loadPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  async function handleDownload() {
    setDownloading(true);
    try {
      const res = await fetch(
        `${API_URL}/pixel-manager/audience-export.csv?days=${days}&includePii=${includePii ? '1' : '0'}`,
        { headers: { Authorization: `Bearer ${getAuthToken()}` } },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const rowsHeader = res.headers.get('X-Export-Rows') || '0';
      const withFbpHeader = res.headers.get('X-Export-With-Fbp') || '0';
      const withEmailHeader = res.headers.get('X-Export-With-Email') || '0';

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `hitzlink-audience-${days}d-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);

      toast.success(
        t('audience.toast.downloaded', {
          rows: rowsHeader,
          fbp: withFbpHeader,
          email: withEmailHeader,
        }),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t('audience.toast.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Explainer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 space-y-1.5">
          <p className="font-medium">{t('audience.explainer.title')}</p>
          <p>{t('audience.explainer.body')}</p>
          <p className="text-xs text-blue-700">{t('audience.explainer.note')}</p>
        </div>
      </div>

      {/* Date range picker */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" /> {t('audience.range.title')}
        </h3>
        <div className="flex items-center gap-2 flex-wrap">
          {presets.map((p) => (
            <button
              key={p.value}
              onClick={() => setDays(p.value)}
              className={`px-4 py-2 text-sm rounded-lg border ${
                days === p.value
                  ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-2 pl-2 border-l border-gray-200">
            <input
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e) => setDays(Math.max(1, Math.min(365, Number(e.target.value) || 30)))}
              className="w-20 px-3 py-2 text-sm border border-gray-300 rounded-lg"
            />
            <span className="text-sm text-gray-500">{t('audience.range.daysSuffix')}</span>
          </div>
        </div>

        <label className="flex items-center gap-2 mt-4 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={includePii}
            onChange={(e) => setIncludePii(e.target.checked)}
            className="rounded"
          />
          {t('audience.range.includePii')}
        </label>
      </div>

      {/* Preview + download */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">{t('audience.preview.label')}</div>
            {loadingPreview ? (
              <div className="text-2xl font-bold text-gray-400 inline-flex items-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('audience.preview.counting')}
              </div>
            ) : (
              <div className="text-2xl font-bold text-gray-900">
                {(preview?.totalClicks || 0).toLocaleString()}{' '}
                <span className="text-base font-normal text-gray-500">
                  {t('audience.preview.clicksWithRange', { days })}
                </span>
              </div>
            )}
            {preview && preview.totalClicks > 0 && (
              <div className="text-xs text-gray-500 mt-1">
                {t('audience.preview.matchEstimate', {
                  value: Math.round(preview.totalClicks * 0.6).toLocaleString(),
                })}
              </div>
            )}
          </div>

          <button
            onClick={handleDownload}
            disabled={downloading || !preview?.totalClicks}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50"
          >
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {t('audience.preview.download')}
          </button>
        </div>

        {preview && preview.totalClicks === 0 && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            {t('audience.preview.empty', { days })}
          </p>
        )}
      </div>

      {/* Upload guide */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600" /> {t('audience.guide.title')}
        </h3>
        <ol className="space-y-2 text-sm text-gray-700 list-decimal list-inside">
          <li>{t('audience.guide.step1')}</li>
          <li>{t('audience.guide.step2')}</li>
          <li>{t('audience.guide.step3')}</li>
          <li>{t('audience.guide.step4')}</li>
          <li>{t('audience.guide.step5')}</li>
          <li>{t('audience.guide.step6')}</li>
          <li>{t('audience.guide.step7')}</li>
        </ol>
      </div>
    </div>
  );
};
